import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import api from './apiClient';
import {
  GALLERY_ID_STORAGE_KEY,
  getGalleryIdForApi,
  setStoredGalleryId,
  clearStoredGalleryId,
} from './galleryStorage';
import { useGalleryIdForApi } from './hooks/useGalleryIdForApi';
import { guestApiPath } from './guestApiPath';
import { getAnonymousUserId } from './utils/anonymousUserId';
import { getUploadErrorDetails } from './utils/uploadErrors';
import { getGuestAuthToken } from './utils/guestToken';
import { getUploadFileType } from './utils/mediaFileType';
import { PROOF_CHECK_UNAVAILABLE_CODE } from './utils/guestContactProof';

export { getGalleryIdForApi, setStoredGalleryId, clearStoredGalleryId, GALLERY_ID_STORAGE_KEY };
export { guestApiPath };

// React Query hooks for data fetching
// New hook using /media endpoint with cursor-based pagination and type filtering
export const useMediaInfinite = (pageSize = 50, type = 'all', options = {}) => {
  const galleryId = useGalleryIdForApi();
  return useInfiniteQuery({
    queryKey: ['media', 'infinite', pageSize, type, galleryId],
    queryFn: async ({ pageParam = null }) => {
      const token = getGuestAuthToken();
      const params = {
        type,
        limit: pageSize,
      };
      if (pageParam) {
        params.cursor = pageParam;
      }
      const response = await api.get(`${guestApiPath('/media', galleryId)}`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      return {
        items: response.data.items,
        nextCursor: response.data.nextCursor || null,
        hasMore: response.data.hasMore || false,
        // Only the first page carries the gallery total; null when unavailable.
        totalCount: Number.isFinite(response.data.totalCount) ? response.data.totalCount : null,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    staleTime: 30 * 1000, // 30 seconds - quick refresh for new uploads
    gcTime: 30 * 60 * 1000, // 30 min — keep in cache for IndexedDB persistence
    refetchOnWindowFocus: false,
    ...options,
  });
};

// Legacy hook for backward compatibility (uses /photos endpoint)
export const usePhotosInfinite = (pageSize = 50, options = {}) => {
  const galleryId = useGalleryIdForApi();
  return useInfiniteQuery({
    queryKey: ['photos', 'infinite', pageSize, galleryId],
    queryFn: async ({ pageParam = 0 }) => {
      const token = getGuestAuthToken();
      const response = await api.get(`${guestApiPath('/photos', galleryId)}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: pageParam, limit: pageSize },
      });
      return {
        photos: response.data.photos,
        pagination: response.data.pagination,
        nextPage: response.data.pagination.hasMore ? pageParam + 1 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 30 * 1000, // 30 seconds - quick refresh for new uploads
    gcTime: 30 * 60 * 1000, // 30 min — keep in cache for IndexedDB persistence
    refetchOnWindowFocus: false,
    ...options,
  });
};

// Keep the old hook for backward compatibility (loads all photos at once)
export const useGuests = (options = {}) => {
  const galleryId = useGalleryIdForApi();
  return useQuery({
    queryKey: ['guests', galleryId],
    queryFn: async () => {
      const token = getGuestAuthToken();
      const response = await api.get(`${guestApiPath('/guests', galleryId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    staleTime: 1 * 60 * 1000, // 1 minute - guest data changes occasionally
    gcTime: 30 * 60 * 1000, // 30 min — keep in cache for IndexedDB persistence
    ...options,
  });
};

export const useAttendanceMutation = () => {
  const queryClient = useQueryClient();
  const galleryId = useGalleryIdForApi();

  return useMutation({
    // `contact` is the email or phone the couple has on file for the guest (issue #277). It goes
    // in the request body only, never the URL, and is not kept anywhere after the request.
    mutationFn: async ({ guestId, attended, contact }) => {
      const token = getGuestAuthToken();
      const response = await api.patch(
        `${guestApiPath(`/guests/${guestId}/check-in`, galleryId)}`,
        { attended, contact },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    // The API's refusals (wrong contact, locked out, preview) are answers, not glitches. Retrying a
    // 4xx would count as another failed proof attempt and delay the message by a second. A 503
    // PROOF_CHECK_UNAVAILABLE is an answer too: the API could not count the attempt and sent its own
    // Retry-After, so retrying after one second would only hit the same contended counter again.
    retry: (failureCount, error) => {
      const status = error?.response?.status;
      if (status >= 400 && status < 500) return false;
      if (error?.response?.data?.error === PROOF_CHECK_UNAVAILABLE_CODE) return false;
      return failureCount < 1;
    },
    onSuccess: (_data, variables) => {
      queryClient.setQueryData(['guests', galleryId], (oldData) => {
        if (!oldData) return oldData;
        return oldData.map((guest) => {
          if (guest.id === variables.guestId) {
            return { ...guest, attended: variables.attended ? 'Y' : 'N' };
          }
          return guest;
        });
      });
      queryClient.invalidateQueries({ queryKey: ['guests'] }, { refetchType: 'active' });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });
};

// Guestbook API hooks
export const useGuestbookInfinite = (pageSize = 50, options = {}) => {
  const galleryId = useGalleryIdForApi();
  return useInfiniteQuery({
    queryKey: ['guestbook', 'infinite', pageSize, galleryId],
    queryFn: async ({ pageParam = null }) => {
      const token = getGuestAuthToken();
      const params = {
        limit: pageSize,
      };
      if (pageParam) {
        params.cursor = pageParam;
      }
      const response = await api.get(`${guestApiPath('/guestbook', galleryId)}`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      return {
        items: response.data.items,
        nextCursor: response.data.nextCursor || null,
        hasMore: response.data.hasMore || false,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    staleTime: 0, // Always fetch latest results - no caching
    gcTime: 30 * 60 * 1000, // 30 min — keep in cache for IndexedDB persistence
    refetchOnWindowFocus: true, // Refetch when window regains focus to get latest
    ...options,
  });
};

export const useCreateGuestbookMutation = () => {
  const queryClient = useQueryClient();
  const galleryId = useGalleryIdForApi();

  return useMutation({
    mutationFn: async ({ name, message }) => {
      const token = getGuestAuthToken();
      const response = await api.post(`${guestApiPath('/guestbook', galleryId)}`, {
        name,
        message,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Optimistically add the new message to the cache
      queryClient.setQueryData(['guestbook', 'infinite', 50, galleryId], (oldData) => {
        if (!oldData) {
          // If no cache exists, create a new one with the message
          return {
            pages: [{
              items: [data],
              nextCursor: null,
              hasMore: false,
            }],
            pageParams: [null],
          };
        }

        // Add the new message to the first page (newest messages appear first)
        const firstPage = oldData.pages[0];
        const updatedFirstPage = {
          ...firstPage,
          items: [data, ...firstPage.items],
        };

        return {
          ...oldData,
          pages: [updatedFirstPage, ...oldData.pages.slice(1)],
        };
      });

      // Invalidate and refetch to ensure consistency with server
      queryClient.invalidateQueries({ queryKey: ['guestbook'] }, { refetchType: 'active' });
    },
    onError: (error) => {
      console.error('Failed to create guestbook entry:', error);
      // Revert optimistic update on error by refetching
      queryClient.invalidateQueries({ queryKey: ['guestbook'] });
    },
  });
};

// Q&A API hooks
export const useQAndAInfinite = (pageSize = 50, anonymousUserId = null, options = {}) => {
  const galleryId = useGalleryIdForApi();
  return useInfiniteQuery({
    queryKey: ['qanda', 'infinite', pageSize, anonymousUserId, galleryId],
    queryFn: async ({ pageParam = null }) => {
      const token = getGuestAuthToken();
      const params = {
        limit: pageSize,
      };
      if (pageParam) {
        params.cursor = pageParam;
      }
      if (anonymousUserId) {
        params.anonymousUserId = anonymousUserId;
      }
      const response = await api.get(`${guestApiPath('/questions', galleryId)}`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      return {
        items: response.data.items,
        nextCursor: response.data.nextCursor || null,
        hasMore: response.data.hasMore || false,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    staleTime: 0, // Always fetch latest results - no caching
    gcTime: 30 * 60 * 1000, // 30 min — keep in cache for IndexedDB persistence
    refetchOnWindowFocus: true, // Refetch when window regains focus to get latest
    ...options,
  });
};

const QANDA_PAGE_SIZE = 50;

export const useCreateQuestionMutation = () => {
  const queryClient = useQueryClient();
  const galleryId = useGalleryIdForApi();

  return useMutation({
    mutationFn: async ({ name, question }) => {
      const token = getGuestAuthToken();
      const response = await api.post(`${guestApiPath('/questions', galleryId)}`, {
        name,
        question,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onMutate: async ({ name, question }) => {
      const anonymousUserId = getAnonymousUserId();
      const qKey = ['qanda', 'infinite', QANDA_PAGE_SIZE, anonymousUserId, galleryId];
      await queryClient.cancelQueries({ queryKey: qKey });
      const previousData = queryClient.getQueryData(qKey);
      const tempId = `optimistic-${Date.now()}`;
      const optimisticItem = {
        id: tempId,
        name: String(name).trim(),
        question: String(question).trim(),
        votes: 0,
        createdAt: Date.now(),
        hasVoted: false,
      };
      queryClient.setQueryData(qKey, (oldData) => {
        if (!oldData) {
          return {
            pages: [{ items: [optimisticItem], nextCursor: null, hasMore: false }],
            pageParams: [null],
          };
        }
        const firstPage = oldData.pages[0];
        return {
          ...oldData,
          pages: [
            { ...firstPage, items: [optimisticItem, ...firstPage.items] },
            ...oldData.pages.slice(1),
          ],
        };
      });
      return { previousData, qKey, tempId };
    },
    onSuccess: (data, _variables, context) => {
      if (!context?.qKey || !context?.tempId) {
        queryClient.invalidateQueries({ queryKey: ['qanda'] }, { refetchType: 'active' });
        return;
      }
      const serverItem = {
        id: data.id,
        name: data.name,
        question: data.question,
        votes: data.votes ?? 0,
        createdAt: data.createdAt,
        hasVoted: false,
      };
      queryClient.setQueryData(context.qKey, (oldData) => {
        if (!oldData) {
          return oldData;
        }
        const [firstPage, ...restPages] = oldData.pages;
        return {
          ...oldData,
          pages: [
            {
              ...firstPage,
              items: firstPage.items.map((item) =>
                item.id === context.tempId ? serverItem : item
              ),
            },
            ...restPages,
          ],
        };
      });
      queryClient.invalidateQueries({ queryKey: ['qanda'] }, { refetchType: 'active' });
    },
    onError: (error, _variables, context) => {
      console.error('Failed to create question:', error);
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(context.qKey, context.previousData);
      } else {
        queryClient.invalidateQueries({ queryKey: ['qanda'] });
      }
    },
  });
};

export const useVoteQuestionMutation = () => {
  const queryClient = useQueryClient();
  const galleryId = useGalleryIdForApi();

  return useMutation({
    mutationFn: async ({ questionId, anonymousUserId }) => {
      const token = getGuestAuthToken();
      const response = await api.post(
        `${guestApiPath(`/questions/${questionId}/vote`, galleryId)}`,
        {
          anonymousUserId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      const qKey = ['qanda', 'infinite', 50, variables.anonymousUserId, galleryId];
      queryClient.setQueryData(qKey, (oldData) => {
        if (!oldData) {
          return oldData;
        }
        const updatedPages = oldData.pages.map((page) => ({
          ...page,
          items: page.items.map((item) => {
            if (item.id === variables.questionId) {
              return {
                ...item,
                votes: (item.votes || 0) + 1,
                hasVoted: true,
              };
            }
            return item;
          }),
        }));
        return { ...oldData, pages: updatedPages };
      });
      queryClient.invalidateQueries({ queryKey: ['qanda'] }, { refetchType: 'active' });
    },
    onError: (error) => {
      console.error('Failed to vote on question:', error);
      queryClient.invalidateQueries({ queryKey: ['qanda'] });
    },
  });
};

export const useUnvoteQuestionMutation = () => {
  const queryClient = useQueryClient();
  const galleryId = useGalleryIdForApi();

  return useMutation({
    mutationFn: async ({ questionId, anonymousUserId }) => {
      const token = getGuestAuthToken();
      const response = await api.delete(
        `${guestApiPath(`/questions/${questionId}/vote`, galleryId)}`,
        {
          params: { anonymousUserId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      const qKey = ['qanda', 'infinite', 50, variables.anonymousUserId, galleryId];
      queryClient.setQueryData(qKey, (oldData) => {
        if (!oldData) {
          return oldData;
        }
        const updatedPages = oldData.pages.map((page) => ({
          ...page,
          items: page.items.map((item) => {
            if (item.id === variables.questionId) {
              return {
                ...item,
                votes: Math.max(0, (item.votes || 0) - 1),
                hasVoted: false,
              };
            }
            return item;
          }),
        }));
        return { ...oldData, pages: updatedPages };
      });
      queryClient.invalidateQueries({ queryKey: ['qanda'] }, { refetchType: 'active' });
    },
    onError: (error) => {
      console.error('Failed to unvote on question:', error);
      queryClient.invalidateQueries({ queryKey: ['qanda'] });
    },
  });
};

// Generate upload URL for a file
export const useUploadUrlMutation = () => {
  const galleryId = useGalleryIdForApi();
  return useMutation({
    retry: (failureCount, error) =>
      failureCount < 1 && getUploadErrorDetails(error).retryable,
    mutationFn: async ({ fileName, contentType, uploadId }) => {
      const token = getGuestAuthToken();
      const requestBody = {
        fileName,
        contentType,
        ...(uploadId ? { uploadId } : {}),
      };
      const response = await api.post(`${guestApiPath('/upload-url', galleryId)}`, requestBody, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });
};

// Generate download URLs for multiple files
export const useDownloadUrlsMutation = () => {
  const galleryId = useGalleryIdForApi();
  return useMutation({
    mutationFn: async ({ fileNames }) => {
      const token = getGuestAuthToken();
      const response = await api.post(`${guestApiPath('/download-urls', galleryId)}`, {
        fileNames,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });
};

// Generate signed view URLs for processed full-size media (lazy load in modal/slideshow)
export const useViewUrlsMutation = () => {
  const galleryId = useGalleryIdForApi();
  return useMutation({
    mutationFn: async ({ fileNames }) => {
      const token = getGuestAuthToken();
      const response = await api.post(`${guestApiPath('/view-urls', galleryId)}`, {
        fileNames,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });
};

// Upload file to signed URL with progress tracking
export const uploadFile = async (url, file, onProgress, onSuccess, signal, requiredHeaders = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let settled = false;

    const rejectOnce = (error) => {
      if (!settled) {
        settled = true;
        reject(error);
      }
    };

    const resolveOnce = () => {
      if (!settled) {
        settled = true;
        resolve();
      }
    };

    const abortUpload = () => {
      xhr.abort();
    };

    if (signal?.aborted) {
      const error = new Error('Upload cancelled');
      error.name = 'AbortError';
      rejectOnce(error);
      return;
    }

    signal?.addEventListener('abort', abortUpload, { once: true });

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Call success callback to invalidate caches
        if (onSuccess) {
          onSuccess();
        }
        resolveOnce();
      } else {
        rejectOnce(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      rejectOnce(new Error('Upload failed'));
    });

    xhr.addEventListener('abort', () => {
      const error = new Error('Upload cancelled');
      error.name = 'AbortError';
      rejectOnce(error);
    });

    xhr.open('PUT', url);
    // The signed URL is bound to the server-normalized content type (e.g. extension-mapped
    // video types), which can differ from the browser-reported `file.type`. Prefer the
    // server's value or GCS rejects the PUT with 403 SignatureDoesNotMatch.
    const requiredEntries = Object.entries(requiredHeaders || {});
    const serverContentType = requiredEntries.find(
      ([name, value]) => name.toLowerCase() === 'content-type' && typeof value === 'string' && value !== ''
    );
    // Without a server value, use the same effective type that was sent to /upload-url (the
    // extension-inferred one when the browser reports a blank or generic type), never an empty one.
    xhr.setRequestHeader(
      'Content-Type',
      serverContentType ? serverContentType[1] : getUploadFileType(file).mimeType || file.type
    );
    requiredEntries.forEach(([name, value]) => {
      if (value == null || value === '') return;
      // Content-Type is set above; keep a single canonical value.
      if (name.toLowerCase() === 'content-type') return;
      xhr.setRequestHeader(name, String(value));
    });
    xhr.send(file);
  });
};

export default api;
